import {
  User,
  Question,
  Flashcard,
  FlashcardDeck,
  Simulado,
  NewsItem,
  UserProgress,
  RecentActivity,
} from "../types";
import { createClient } from "@supabase/supabase-js";
import { getLevelFromXp } from "../constants";

// =========================
// SUPABASE CLIENT (Vite)
// =========================
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Supabase env vars missing. Check .env: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY"
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

class DatabaseService {
  // =========================
  // HELPERS
  // =========================
  private mapUser(u: any): User {
    // Opção B: override manual via coluna users.is_premium
    const premiumOverride = u?.is_premium === true;

    return {
      id: u.id,
      email: u.email ?? "",
      name: u.name ?? "",
      xp: u.xp ?? 0,
      level: u.level ?? 1,
      role: (u.role ?? "user") as "user" | "admin",
      subscriptionStatus: (premiumOverride
        ? "premium"
        : (u.subscription_status ?? "free")) as "free" | "premium",
      subscriptionPlan: u.subscription_plan ?? undefined,
      subscriptionEndDate: u.subscription_end_date ?? undefined,
      createdAt: u.created_at ?? undefined,
    };
  }

  // Pega o usuário logado (uuid) do Supabase Auth
  private async requireAuthUserId(): Promise<string> {
    const { data, error } = await supabase.auth.getUser(); // valida na rede [web:3061]
    if (error || !data?.user?.id) {
      throw new Error(error?.message || "Not authenticated");
    }
    return data.user.id;
  }

  // =========================
  // USERS (perfil em public.users)
  // =========================
  async getMyProfile(): Promise<User | null> {
    try {
      const uid = await this.requireAuthUserId();
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", uid)
        .maybeSingle();

      if (error || !data) return null;
      return this.mapUser(data);
    } catch {
      return null;
    }
  }

  async getUserById(id: string): Promise<User | null> {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error || !data) return null;
    return this.mapUser(data);
  }

  // Atualiza XP do usuário logado + progresso/atividade
  async addXP(
    amount: number,
    themeId?: number,
    activityTitle?: string
  ): Promise<User | null> {
    const uid = await this.requireAuthUserId();

    const me = await this.getUserById(uid);
    if (!me) return null;

    const newXp = (me.xp || 0) + amount;
    const levelInfo = getLevelFromXp(newXp);

    const { data: updatedUser, error: userError } = await supabase
      .from("users")
      .update({ xp: newXp, level: levelInfo.level })
      .eq("id", uid)
      .select("*")
      .single();

    if (userError || !updatedUser) throw userError;

    // Atualiza progresso do tema
    if (themeId) {
      const { data: currentProg, error: progReadErr } = await supabase
        .from("user_progress")
        .select("*")
        .eq("user_id", uid)
        .eq("theme_id", themeId)
        .maybeSingle();

      if (progReadErr) throw progReadErr;

      if (currentProg) {
        const { error: progUpdateErr } = await supabase
          .from("user_progress")
          .update({
            xp_earned: (currentProg.xp_earned ?? 0) + amount,
            questions_completed:
              amount >= 10
                ? (currentProg.questions_completed ?? 0) + 1
                : (currentProg.questions_completed ?? 0),
          })
          .eq("id", currentProg.id);

        if (progUpdateErr) throw progUpdateErr;
      } else {
        const { error: progInsertErr } = await supabase
          .from("user_progress")
          .insert({
            user_id: uid,
            theme_id: themeId,
            xp_earned: amount,
            questions_completed: amount >= 10 ? 1 : 0,
          });

        if (progInsertErr) throw progInsertErr;
      }
    }

    // Registra atividade
    const { error: actErr } = await supabase.from("activities").insert({
      user_id: uid,
      type: (levelInfo.level > me.level ? "level" : "question") as any,
      title: activityTitle || "Atividade Realizada",
      subtitle: `+${amount} XP`,
      xp_gain: amount,
      timestamp: new Date().toISOString(),
    });

    if (actErr) throw actErr;

    return this.mapUser(updatedUser);
  }

  async upgradeToPremium(plan: "mensal" | "anual"): Promise<User | null> {
    const uid = await this.requireAuthUserId();

    const endDate = new Date();
    if (plan === "anual") endDate.setFullYear(endDate.getFullYear() + 1);
    if (plan === "mensal") endDate.setMonth(endDate.getMonth() + 1);

    const { data, error } = await supabase
      .from("users")
      .update({
        // Opção B: também marca a flag manual (mantém consistência)
        is_premium: true,
        subscription_status: "premium",
        subscription_plan: plan,
        subscription_end_date: endDate.toISOString(),
      })
      .eq("id", uid)
      .select("*")
      .single();

    if (error || !data) throw error;
    return this.mapUser(data);
  }

  // =========================
  // QUESTIONS (conteúdo global)
  // =========================
  async getQuestions(user?: User): Promise<Question[]> {
    let query = supabase
      .from("questions")
      .select("*")
      .order("id", { ascending: false });

    if (user && user.role !== "admin" && user.subscriptionStatus !== "premium") {
      query = query.eq("is_premium", false);
    }

    const { data, error } = await query;
    if (error) return [];

    return (data || []).map((q: any) => ({
      id: q.id,
      themeId: q.theme_id,
      subtheme: q.subtheme,
      title: q.title,
      statement: q.statement,
      imageUrl: q.image_url,
      optionA: q.option_a,
      optionB: q.option_b,
      optionC: q.option_c,
      optionD: q.option_d,
      correctOption: q.correct_option,
      explanation: q.explanation,
      difficulty: q.difficulty,
      xpReward: q.xp_reward,
      isPremium: q.is_premium,
    }));
  }

  async addQuestion(q: Omit<Question, "id">): Promise<void> {
    await this.requireAuthUserId();

    const { error } = await supabase.from("questions").insert([
      {
        theme_id: q.themeId,
        subtheme: q.subtheme,
        title: q.title,
        statement: q.statement,
        image_url: q.imageUrl,
        option_a: q.optionA,
        option_b: q.optionB,
        option_c: q.optionC,
        option_d: q.optionD,
        correct_option: q.correctOption,
        explanation: q.explanation,
        difficulty: q.difficulty,
        xp_reward: q.xpReward,
        is_premium: q.isPremium,
      },
    ]);

    if (error) throw error;
  }

  async updateQuestion(id: number, q: Partial<Question>): Promise<void> {
    await this.requireAuthUserId();

    const updateData: any = {};
    if (q.themeId !== undefined) updateData.theme_id = q.themeId;
    if (q.subtheme !== undefined) updateData.subtheme = q.subtheme;
    if (q.title !== undefined) updateData.title = q.title;
    if (q.statement !== undefined) updateData.statement = q.statement;
    if (q.imageUrl !== undefined) updateData.image_url = q.imageUrl;
    if (q.optionA !== undefined) updateData.option_a = q.optionA;
    if (q.optionB !== undefined) updateData.option_b = q.optionB;
    if (q.optionC !== undefined) updateData.option_c = q.optionC;
    if (q.optionD !== undefined) updateData.option_d = q.optionD;
    if (q.correctOption !== undefined) updateData.correct_option = q.correctOption;
    if (q.explanation !== undefined) updateData.explanation = q.explanation;
    if (q.difficulty !== undefined) updateData.difficulty = q.difficulty;
    if (q.xpReward !== undefined) updateData.xp_reward = q.xpReward;
    if (q.isPremium !== undefined) updateData.is_premium = q.isPremium;

    const { error } = await supabase
      .from("questions")
      .update(updateData)
      .eq("id", id);

    if (error) throw error;
  }

  async deleteQuestion(id: number): Promise<void> {
    await this.requireAuthUserId();
    const { error } = await supabase.from("questions").delete().eq("id", id);
    if (error) throw error;
  }

  // =========================
  // NEWS (conteúdo global)
  // =========================
  async getNews(user?: User): Promise<NewsItem[]> {
    let query = supabase
      .from("news")
      .select("*")
      .order("is_fixed", { ascending: false })
      .order("created_at", { ascending: false });

    if (user && user.role !== "admin" && user.subscriptionStatus !== "premium") {
      query = query.eq("is_premium", false);
    }

    const { data, error } = await query;
    if (error) return [];

    return (data || []).map((n: any) => ({
      id: n.id,
      title: n.title,
      url: n.url,
      summary: n.summary,
      content: n.content,
      themeId: n.theme_id,
      createdAt: n.created_at,
      isPremium: n.is_premium,
      isFixed: n.is_fixed,
    }));
  }

  async addNews(n: Omit<NewsItem, "id" | "createdAt">): Promise<void> {
    await this.requireAuthUserId();

    const { error } = await supabase.from("news").insert([
      {
        title: n.title,
        url: n.url,
        summary: n.summary,
        content: n.content,
        theme_id: n.themeId,
        is_premium: n.isPremium,
        is_fixed: n.isFixed,
      },
    ]);

    if (error) throw error;
  }

  async updateNews(id: number, n: Partial<NewsItem>): Promise<void> {
    await this.requireAuthUserId();

    const updateData: any = {};
    if (n.title !== undefined) updateData.title = n.title;
    if (n.url !== undefined) updateData.url = n.url;
    if (n.summary !== undefined) updateData.summary = n.summary;
    if (n.content !== undefined) updateData.content = n.content;
    if (n.themeId !== undefined) updateData.theme_id = n.themeId;
    if (n.isPremium !== undefined) updateData.is_premium = n.isPremium;
    if (n.isFixed !== undefined) updateData.is_fixed = n.isFixed;

    const { error } = await supabase.from("news").update(updateData).eq("id", id);
    if (error) throw error;
  }

  async deleteNews(id: number): Promise<void> {
    await this.requireAuthUserId();
    const { error } = await supabase.from("news").delete().eq("id", id);
    if (error) throw error;
  }

  // =========================
  // FLASHCARDS (conteúdo global)
  // =========================
  async getDecks(): Promise<FlashcardDeck[]> {
    const { data, error } = await supabase.from("decks").select("*");
    if (error) return [];
    return (data || []).map((d: any) => ({
      id: d.id,
      themeId: d.theme_id,
      name: d.name,
      description: d.description,
      isPremium: d.is_premium,
    }));
  }

  async getFlashcards(deckId: number): Promise<Flashcard[]> {
    const { data, error } = await supabase
      .from("flashcards")
      .select("*")
      .eq("deck_id", deckId);

    if (error) return [];
    return (data || []).map((f: any) => ({
      id: f.id,
      deckId: f.deck_id,
      subtheme: f.subtheme,
      front: f.front,
      frontImageUrl: f.front_image_url,
      back: f.back,
      backImageUrl: f.back_image_url,
      xpReward: f.xp_reward,
    }));
  }

  // sem .select() (evita depender de SELECT no retorno)
  async addFlashcard(f: Omit<Flashcard, "id">): Promise<void> {
    await this.requireAuthUserId();

    const { error } = await supabase.from("flashcards").insert([
      {
        deck_id: f.deckId,
        subtheme: f.subtheme,
        front: f.front,
        front_image_url: f.frontImageUrl ?? null,
        back: f.back,
        back_image_url: f.backImageUrl ?? null,
        xp_reward: f.xpReward ?? 0,
      },
    ]);

    if (error) throw error;
  }

  // sem .select() (mas UPDATE ainda pode exigir SELECT policy via RLS)
  async updateFlashcard(id: number, f: Partial<Flashcard>): Promise<void> {
    await this.requireAuthUserId();

    const updateData: any = {};
    if (f.deckId !== undefined) updateData.deck_id = f.deckId;
    if (f.subtheme !== undefined) updateData.subtheme = f.subtheme;
    if (f.front !== undefined) updateData.front = f.front;
    if (f.frontImageUrl !== undefined) updateData.front_image_url = f.frontImageUrl;
    if (f.back !== undefined) updateData.back = f.back;
    if (f.backImageUrl !== undefined) updateData.back_image_url = f.backImageUrl;
    if (f.xpReward !== undefined) updateData.xp_reward = f.xpReward;

    const { error } = await supabase
      .from("flashcards")
      .update(updateData)
      .eq("id", id);

    if (error) throw error;
  }

  async deleteFlashcard(id: number): Promise<void> {
    await this.requireAuthUserId();
    const { error } = await supabase.from("flashcards").delete().eq("id", id);
    if (error) throw error;
  }

  // =========================
  // FLASHCARDS (progresso do usuário)
  // =========================
  async insertFlashcardReview(input: {
    user_id: string;          // uuid (auth.users.id) em formato string (será ignorado e substituído pelo uid real)
    deck_id: number;          // integer (decks.id)
    flashcard_id: number;     // integer (flashcards.id)
    subtema: string | null;   // text
    is_correct: boolean;
    duration_seconds: number; // int
  }): Promise<void> {
    // Usa SEMPRE o usuário da sessão para evitar mismatch/RLS [web:2666]
    const uid = await this.requireAuthUserId();

    const { error } = await supabase
      .from("user_flashcard_reviews")
      .insert([
        {
          user_id: uid,
          deck_id: input.deck_id,
          flashcard_id: input.flashcard_id,
          subtema: input.subtema,
          is_correct: input.is_correct,
          duration_seconds: input.duration_seconds,
        },
      ]); // insert padrão [web:3072]

    if (error) throw error;
  }

  async getDeckSubtemaStats(deckId: number): Promise<Array<{
    subtema: string;
    total_seconds: number;
    accuracy: number | null;
    attempts: number;
  }>> {
    const uid = await this.requireAuthUserId();

    const { data, error } = await supabase
      .from("vw_deck_subtema_stats")
      .select("subtema,total_seconds,accuracy,attempts") // select padrão [web:3075]
      .eq("deck_id", deckId) // filtro eq [web:3085]
      .eq("user_id", uid)
      .order("total_seconds", { ascending: false });

    if (error) throw error;
    return (data || []) as any;
  }

  // =========================
  // SIMULADOS (conteúdo global)
  // =========================
  async getSimulados(userId?: string): Promise<Simulado[]> {
    let query = supabase.from("simulados").select("*");

    if (userId) {
      query = query.or(`author_id.eq.${userId},author_id.is.null`);
    } else {
      query = query.is("author_id", null);
    }

    const { data, error } = await query;
    if (error) return [];

    return (data || []).map((s: any) => ({
      id: s.id,
      title: s.title,
      themeId: s.theme_id,
      questionIds: s.question_ids,
      durationMinutes: s.duration_minutes,
      isPremium: s.is_premium,
      authorId: s.author_id ?? undefined,
    }));
  }

  async getAdminSimulados(): Promise<Simulado[]> {
    const { data, error } = await supabase
      .from("simulados")
      .select("*")
      .is("author_id", null);

    if (error) return [];

    return (data || []).map((s: any) => ({
      id: s.id,
      title: s.title,
      themeId: s.theme_id,
      questionIds: s.question_ids,
      durationMinutes: s.duration_minutes,
      isPremium: s.is_premium,
      authorId: s.author_id ?? undefined,
    }));
  }

  async addSimulado(s: Omit<Simulado, "id">): Promise<void> {
    await this.requireAuthUserId();

    const { error } = await supabase.from("simulados").insert([
      {
        title: s.title,
        theme_id: s.themeId,
        question_ids: s.questionIds ?? [],
        duration_minutes: s.durationMinutes,
        is_premium: s.isPremium,
        author_id: s.authorId ?? null,
      },
    ]);

    if (error) throw error;
  }

  async updateSimulado(id: number, s: Partial<Simulado>): Promise<void> {
    await this.requireAuthUserId();

    const updateData: any = {};
    if (s.title !== undefined) updateData.title = s.title;
    if (s.themeId !== undefined) updateData.theme_id = s.themeId;
    if (s.questionIds !== undefined) updateData.question_ids = s.questionIds;
    if (s.durationMinutes !== undefined)
      updateData.duration_minutes = s.durationMinutes;
    if (s.isPremium !== undefined) updateData.is_premium = s.isPremium;
    if (s.authorId !== undefined) updateData.author_id = s.authorId ?? null;

    const { error } = await supabase
      .from("simulados")
      .update(updateData)
      .eq("id", id);

    if (error) throw error;
  }

  async deleteSimulado(id: number): Promise<void> {
    await this.requireAuthUserId();
    const { error } = await supabase.from("simulados").delete().eq("id", id);
    if (error) throw error;
  }

  // =========================
  // PROGRESS & ACTIVITIES (privado por usuário)
  // =========================
  async getUserProgress(userId: string): Promise<UserProgress[]> {
    const { data, error } = await supabase
      .from("user_progress")
      .select("*")
      .eq("user_id", userId);

    if (error) return [];

    return (data || []).map((p: any) => ({
      themeId: p.theme_id,
      questionsCompleted: p.questions_completed,
      xpEarned: p.xp_earned,
    }));
  }

  async getMyProgress(): Promise<UserProgress[]> {
    const uid = await this.requireAuthUserId();
    return this.getUserProgress(uid);
  }

  async getActivities(userId: string): Promise<RecentActivity[]> {
    const { data, error } = await supabase
      .from("activities")
      .select("*")
      .eq("user_id", userId)
      .order("timestamp", { ascending: false });

    if (error) return [];

    return (data || []).map((a: any) => ({
      id: a.id,
      userId: a.user_id,
      type: a.type,
      title: a.title,
      subtitle: a.subtitle,
      xpGain: a.xp_gain ?? undefined,
      timestamp: a.timestamp,
    }));
  }

  async getMyActivities(): Promise<RecentActivity[]> {
    const uid = await this.requireAuthUserId();
    return this.getActivities(uid);
  }
}

export const dbService = new DatabaseService();
